import { montarContexto, renderizar, VARIAVEIS, type CanalCampanha, type FiltrosPublico } from '@crm/contracts';
import { corpoEmailFinal, corpoEmailVazio, EditorEmail, paraHtmlEmail, PreviaEmailHtml } from './EditorEmail';
import { botaoWhatsappInicial, botaoWhatsappPayload, CamposBotaoWhatsapp, textoBotaoExemplo, type BotaoWhatsappValor } from './BotaoWhatsapp';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, apiBlob, mensagemErro } from '../api';
import { porGrupo, useStatusClientes } from '../status';
import { useAuth } from '../auth';
import { Carregando, Erro } from '../components/ui';
import { hojeISO } from '../format';
import { lerDataUrl, prepararImagem } from '../imagem';
import { useTags, useUsuarios } from './Clientes';
import { useTemplates } from './Templates';

interface Campanha {
  id: string; nome: string; mensagem: string; filtros: FiltrosPublico; atribuicao: 'VENDEDOR_DO_CLIENTE' | 'CRIADOR';
  dataInicio: string | null; dataFim: string | null; status: string; temImagem: boolean; templateId?: string | null;
  canal: CanalCampanha; canalAlternativo: boolean; emailAssunto: string | null; emailCorpo: string | null; emailTemplateId: string | null;
  whatsappBotao?: boolean; whatsappTexto?: string | null; whatsappMensagem?: string | null; whatsappStatusId?: string | null; whatsappProtegeAtivo?: boolean;
}

const CANAIS: Array<{ id: CanalCampanha; label: string; texto: string }> = [
  { id: 'WHATSAPP', label: 'WhatsApp', texto: 'Todos recebem por WhatsApp, pela fila do vendedor (como hoje).' },
  { id: 'EMAIL', label: 'E-mail', texto: 'Todos recebem por e-mail, enviado automaticamente pela caixa de cada vendedor.' },
  { id: 'PREFERENCIA', label: 'Preferência de cada cliente', texto: 'Cada cliente recebe pelo canal marcado na ficha; sem preferência, pelo canal padrão da empresa.' },
];

const MSG_PADRAO = 'Olá {{primeiro_nome}}, tudo bem? Aqui é {{vendedor}}, da {{empresa_vendedora}}. Separei condições deste mês para {{empresa|sua empresa}}. Posso te mandar a tabela?';

export function CampanhaFormPage() {
  const { id } = useParams<{ id: string }>();
  const q = useQuery({ queryKey: ['campanha', id], queryFn: () => api<Campanha>(`/campanhas/${id}`), enabled: !!id });
  if (id && q.isLoading) return <Carregando />;
  if (id && q.error) return <Erro erro={mensagemErro(q.error)} />;
  return <Form inicial={q.data} />;
}

function Form({ inicial }: { inicial?: Campanha }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { me, pode } = useAuth();
  const tags = useTags();
  const statusLista = useStatusClientes();
  const usuarios = useUsuarios(pode('CLIENTES_ATRIBUIR'));
  const ref = useRef<HTMLTextAreaElement>(null);
  const [nome, setNome] = useState(inicial?.nome ?? '');
  const [mensagem, setMensagem] = useState(inicial?.mensagem ?? MSG_PADRAO);
  // "Nova campanha para quem clicou" (specs/24): já vem filtrada pelo status e por e-mail
  const [sp] = useSearchParams();
  const statusDaUrl = !inicial ? sp.get('statusId') : null;
  const [f, setF] = useState<FiltrosPublico>(inicial?.filtros ?? (statusDaUrl ? { statusIds: [statusDaUrl] } : { status: ['ATIVO'] }));
  const [atribuicao, setAtribuicao] = useState(inicial?.atribuicao ?? 'VENDEDOR_DO_CLIENTE');
  const [dataInicio, setDataInicio] = useState(inicial?.dataInicio ?? '');
  const [dataFim, setDataFim] = useState(inicial?.dataFim ?? '');
  // undefined = sem mudança; null = remover; string = nova imagem (data URL)
  const [novaImagem, setNovaImagem] = useState<string | null | undefined>(undefined);
  const [imagemAtual, setImagemAtual] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const templates = useTemplates('WHATSAPP');
  const templatesEmail = useTemplates('EMAIL');
  const [templateId, setTemplateId] = useState<string | null>(inicial?.templateId ?? null);
  const [canal, setCanal] = useState<CanalCampanha>(inicial?.canal ?? (sp.get('canal') === 'EMAIL' ? 'EMAIL' : 'WHATSAPP'));
  const [canalAlternativo, setCanalAlternativo] = useState(inicial?.canalAlternativo ?? true);
  const [emailAssunto, setEmailAssunto] = useState(inicial?.emailAssunto ?? '');
  const [emailCorpo, setEmailCorpo] = useState(() => paraHtmlEmail(inicial?.emailCorpo));
  const [emailTemplateId, setEmailTemplateId] = useState<string | null>(inicial?.emailTemplateId ?? null);
  // botão "Falar no WhatsApp" no e-mail (specs/24)
  const [wa, setWa] = useState<BotaoWhatsappValor>(() => botaoWhatsappInicial(inicial));
  const usaWhatsapp = canal !== 'EMAIL';
  const usaEmail = canal !== 'WHATSAPP';

  const usarTemplateEmail = (id: string) => {
    const t = templatesEmail.data?.find((x) => x.id === id);
    if (!t) return;
    setEmailTemplateId(t.id);
    setEmailAssunto(t.assunto ?? '');
    setEmailCorpo(paraHtmlEmail(t.corpo));
    // o botão WhatsApp do template vem junto (specs/24)
    setWa(botaoWhatsappInicial(t));
  };
  async function usarTemplate(id: string) {
    const t = templates.data?.find((x) => x.id === id);
    if (!t) return;
    setTemplateId(t.id);
    setMensagem(t.corpo);
    if (t.temImagem) {
      try { setNovaImagem(await lerDataUrl(await apiBlob(`/templates/${t.id}/imagem`))); } catch { /* segue sem imagem */ }
    }
  }

  useEffect(() => {
    if (inicial && inicial.status !== 'RASCUNHO') nav(`/campanhas/${inicial.id}`, { replace: true });
  }, [inicial, nav]);

  useEffect(() => {
    if (!inicial?.temImagem) return;
    let url: string | null = null;
    apiBlob(`/campanhas/${inicial.id}/imagem`)
      .then((b) => setImagemAtual((url = URL.createObjectURL(b))))
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [inicial?.id, inicial?.temImagem]);

  async function escolherImagem(arquivo: File | undefined) {
    if (!arquivo) return;
    setErro(null);
    try {
      setNovaImagem(await prepararImagem(arquivo));
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  }
  const previewImagem = novaImagem === undefined ? imagemAtual : novaImagem;

  const inserir = (v: string) => {
    const el = ref.current;
    const token = `{{${v}}}`;
    if (!el) return setMensagem(mensagem + token);
    const ini = el.selectionStart ?? mensagem.length;
    const fim = el.selectionEnd ?? mensagem.length;
    setMensagem(mensagem.slice(0, ini) + token + mensagem.slice(fim));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(ini + token.length, ini + token.length); });
  };

  const ctxExemplo = montarContexto({
    cliente: { nome: 'João Batista', empresa: 'Comercial São Jorge', cidade: 'São Paulo', ultimaCompra: '2026-09-07', valorUltimaCompra: 1840 },
    vendedorNome: me?.usuario.nome, empresaVendedora: me?.empresa.nome, hoje: hojeISO(),
  });
  const exemplo = renderizar(mensagem, ctxExemplo);
  const exemploAssunto = renderizar(emailAssunto, ctxExemplo);
  const emailFinal = corpoEmailFinal(emailCorpo);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    if (usaEmail && corpoEmailVazio(emailFinal)) return setErro('Escreva o texto do e-mail.');
    setSalvando(true);
    const filtros: FiltrosPublico = Object.fromEntries(
      Object.entries(f).filter(([, v]) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)),
    );
    const body = {
      nome, filtros, atribuicao, dataInicio, dataFim, canal, canalAlternativo,
      mensagem: usaWhatsapp ? mensagem : '', templateId: usaWhatsapp ? templateId : null,
      emailAssunto: usaEmail ? emailAssunto : null, emailCorpo: usaEmail ? emailFinal : null, emailTemplateId: usaEmail ? emailTemplateId : null,
      ...botaoWhatsappPayload(wa, usaEmail),
    };
    try {
      const r = inicial
        ? await api<Campanha>(`/campanhas/${inicial.id}`, { method: 'PATCH', body })
        : await api<Campanha>('/campanhas', { body });
      if (usaWhatsapp && typeof novaImagem === 'string') await api(`/campanhas/${r.id}/imagem`, { method: 'PUT', body: { dataUrl: novaImagem } });
      else if (novaImagem === null && inicial?.temImagem) await api(`/campanhas/${r.id}/imagem`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: ['campanhas'] });
      await qc.invalidateQueries({ queryKey: ['campanha', r.id] });
      nav(`/campanhas/${r.id}`);
    } catch (err) {
      setErro(mensagemErro(err));
    } finally {
      setSalvando(false);
    }
  }

  const toggle = <T,>(lista: T[] | undefined, v: T) => (lista?.includes(v) ? lista.filter((x) => x !== v) : [...(lista ?? []), v]);

  return (
    <form className="col" onSubmit={submit} style={{ gap: 18 }}>
      <Link to="/campanhas" className="small">← Campanhas</Link>
      <div className="page-head">
        <h1>{inicial ? 'Editar campanha' : 'Nova campanha'}</h1>
        <div className="actions">
          <button className="btn primary" disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar e revisar público'}</button>
        </div>
      </div>
      <Erro erro={erro} />

      <div className="row" style={{ gap: 20 }}>
        <div className="col" style={{ flex: '1.4 1 520px' }}>
          <section className="card">
            <label className="field"><span>Nome da campanha *</span><input className="input" required minLength={2} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Promoção Outubro" /></label>
          </section>

          <section className="card" aria-labelledby="h-canal">
            <h2 id="h-canal">Canal</h2>
            <div className="seg" role="group" aria-label="Canal da campanha">
              {CANAIS.map((c) => <button key={c.id} type="button" aria-pressed={canal === c.id} onClick={() => setCanal(c.id)}>{c.label}</button>)}
            </div>
            <span className="small muted">{CANAIS.find((c) => c.id === canal)!.texto}</span>
            <label className="check small">
              <input type="checkbox" checked={canalAlternativo} onChange={(e) => setCanalAlternativo(e.target.checked)} />
              {canal === 'WHATSAPP' ? 'Quem não tem WhatsApp recebe por e-mail' : canal === 'EMAIL' ? 'Quem não tem e-mail válido recebe por WhatsApp' : 'Quem não tem o canal preferido recebe pelo outro'}
            </label>
            {usaEmail && <span className="small faint">Os e-mails saem sozinhos pela caixa de cada vendedor, respeitando os limites por hora e por dia (Configurações › E-mail de saída).</span>}
          </section>

          {usaWhatsapp && <>
          <section className="card">
            <div className="card-head"><h2>{usaEmail ? 'Mensagem do WhatsApp' : 'Mensagem'}</h2><span className="small num faint">{mensagem.length} / 1000</span></div>
            {!!templates.data?.length && (
              <label className="field"><span>Usar template</span>
                <select className="select" value={templateId ?? ''} onChange={(e) => (e.target.value ? void usarTemplate(e.target.value) : setTemplateId(null))}>
                  <option value="">Escrever do zero</option>
                  {templates.data.map((t) => <option key={t.id} value={t.id}>{t.nome}{t.temImagem ? ' · com imagem' : ''}</option>)}
                </select>
              </label>
            )}
            <textarea ref={ref} className="textarea" rows={5} maxLength={1000} required={usaWhatsapp} value={mensagem} onChange={(e) => setMensagem(e.target.value)} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span className="small faint">Inserir:</span>
              {VARIAVEIS.map((v) => <button type="button" key={v} className="pill num" style={{ height: 28, fontSize: 12 }} onClick={() => inserir(v)}>{v}</button>)}
            </div>
            <span className="small faint">Use {'{{variavel|texto padrão}}'} para quando o cliente não tiver o dado. Ex.: {'{{empresa|sua empresa}}'}.</span>
            <div className="field"><span>Exemplo (cliente fictício)</span>
              <div className="bubble" style={{ alignSelf: 'flex-end', background: '#173A2E', color: '#E7F6EF', border: 0 }}>{exemplo.texto || '—'}</div>
              {exemplo.faltando.length > 0 && <span className="hint warn">Sem valor e sem texto padrão: {exemplo.faltando.join(', ')}</span>}
            </div>
          </section>

          <section className="card">
            <div className="card-head"><h2>Imagem</h2><span className="small faint">opcional</span></div>
            {previewImagem
              ? <img src={previewImagem} alt="Imagem da campanha" style={{ maxHeight: 240, maxWidth: '100%', objectFit: 'contain', alignSelf: 'flex-start', borderRadius: 10 }} />
              : <span className="small muted">Nenhuma imagem.</span>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label className="btn">
                {previewImagem ? 'Trocar imagem' : 'Escolher imagem'}
                <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => { void escolherImagem(e.target.files?.[0]); e.target.value = ''; }} />
              </label>
              {previewImagem && <button type="button" className="btn" onClick={() => setNovaImagem(null)}>Remover</button>}
            </div>
            <span className="small faint">Ao abrir o WhatsApp pela fila, a imagem é copiada automaticamente: o vendedor só aperta Ctrl+V na conversa.</span>
          </section>
          </>}

          {usaEmail && (
            <section className="card" aria-labelledby="h-email">
              <div className="card-head"><h2 id="h-email">E-mail</h2><span className="small num faint">{emailFinal.length.toLocaleString('pt-BR')} / 50.000</span></div>
              {!!templatesEmail.data?.length && (
                <label className="field"><span>Usar template</span>
                  <select className="select" value={emailTemplateId ?? ''} onChange={(e) => (e.target.value ? usarTemplateEmail(e.target.value) : setEmailTemplateId(null))}>
                    <option value="">Escrever do zero</option>
                    {templatesEmail.data.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                  </select>
                </label>
              )}
              <label className="field"><span>Assunto *</span><input className="input" required={usaEmail} maxLength={200} value={emailAssunto} onChange={(e) => setEmailAssunto(e.target.value)} /></label>
              <div className="field"><span>Texto *</span>
                <EditorEmail valor={emailCorpo} onChange={setEmailCorpo} rotulo="Texto do e-mail" />
              </div>
              <div className="field"><span>Exemplo (cliente fictício)</span>
                <PreviaEmailHtml html={emailFinal} ctx={ctxExemplo} assunto={exemploAssunto.texto}>
                  {wa.botao && <div><span className="previa-botao-wa">{textoBotaoExemplo(wa, ctxExemplo)}</span></div>}
                  <div className="assinatura">Assinatura de cada vendedor (texto e imagem) · link de descadastro</div>
                </PreviaEmailHtml>
              </div>

              <CamposBotaoWhatsapp valor={wa} onChange={setWa} ctxExemplo={ctxExemplo} />
            </section>
          )}
        </div>

        <div className="col" style={{ flex: '1 1 360px' }}>
          <section className="card">
            <h2>Público</h2>
            <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <legend className="small muted" style={{ marginBottom: 8, padding: 0 }}>Status do cliente</legend>
              <div className="col" style={{ gap: 8, width: '100%' }}>
                {porGrupo(statusLista.data).map((g) => {
                  const grupoOn = !!f.status?.includes(g.grupo);
                  return (
                    <div key={g.grupo} className="row" style={{ gap: 6 }}>
                      <button type="button" className={`pill${grupoOn ? ' on' : ''}`} aria-pressed={grupoOn}
                        title={`Todos os status do grupo ${g.nome}`}
                        onClick={() => setF({
                          ...f,
                          status: toggle(f.status, g.grupo),
                          // grupo inteiro marcado: os status dele já estão incluídos
                          statusIds: grupoOn ? f.statusIds : f.statusIds?.filter((id) => !g.itens.some((s) => s.id === id)),
                        })}>{g.nome}{g.itens.length > 1 ? ' (todos)' : ''}</button>
                      {g.itens.length > 1 && g.itens.map((st) => {
                        const on = grupoOn || !!f.statusIds?.includes(st.id);
                        return (
                          <button type="button" key={st.id} className={`pill${on ? ' on' : ''}`} aria-pressed={on} disabled={grupoOn}
                            onClick={() => setF({ ...f, statusIds: toggle(f.statusIds, st.id) })}>{st.nome}</button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </fieldset>
            {!!tags.data?.length && (
              <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <legend className="small muted" style={{ marginBottom: 8, padding: 0 }}>Com alguma destas tags</legend>
                {tags.data.map((t) => (
                  <button type="button" key={t.id} className={`pill${f.tagIds?.includes(t.id) ? ' on' : ''}`} aria-pressed={!!f.tagIds?.includes(t.id)}
                    onClick={() => setF({ ...f, tagIds: toggle(f.tagIds, t.id) })}>{t.nome}</button>
                ))}
              </fieldset>
            )}
            <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              <label className="field"><span>Sem comprar há mais de (dias)</span>
                <input className="input num" type="number" min={1} value={f.semCompraHaDias ?? ''} onChange={(e) => setF({ ...f, semCompraHaDias: e.target.value ? Number(e.target.value) : undefined })} />
              </label>
              <label className="field"><span>Sem contato há mais de (dias)</span>
                <input className="input num" type="number" min={1} value={f.semContatoHaDias ?? ''} onChange={(e) => setF({ ...f, semContatoHaDias: e.target.value ? Number(e.target.value) : undefined })} />
              </label>
            </div>
            {f.semCompraHaDias && (
              <label className="check small"><input type="checkbox" checked={!!f.incluirSemCompra} onChange={(e) => setF({ ...f, incluirSemCompra: e.target.checked })} />Incluir clientes sem compra registrada</label>
            )}
            <label className="field"><span>Cidade</span><input className="input" value={f.cidade ?? ''} onChange={(e) => setF({ ...f, cidade: e.target.value || undefined })} /></label>
            {pode('CLIENTES_ATRIBUIR') && (
              <label className="field"><span>Somente da carteira de</span>
                <select className="select" value={f.vendedorId ?? ''} onChange={(e) => setF({ ...f, vendedorId: e.target.value || undefined })}>
                  <option value="">Todos os vendedores</option>
                  {usuarios.data?.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
                </select>
              </label>
            )}
            <span className="small faint">Clientes marcados como NÃO CONTACTAR são excluídos automaticamente.</span>
          </section>

          <section className="card">
            <h2>Execução</h2>
            <fieldset style={{ border: 0, margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <legend className="small muted" style={{ marginBottom: 8, padding: 0 }}>Quem envia</legend>
              <label className="check"><input type="radio" name="atrib" checked={atribuicao === 'VENDEDOR_DO_CLIENTE'} onChange={() => setAtribuicao('VENDEDOR_DO_CLIENTE')} />Vendedor de cada cliente</label>
              <label className="check"><input type="radio" name="atrib" checked={atribuicao === 'CRIADOR'} onChange={() => setAtribuicao('CRIADOR')} />Eu envio para todos</label>
            </fieldset>
            <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
              <label className="field"><span>Início</span><input className="input" type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} /></label>
              <label className="field"><span>Fim</span><input className="input" type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} /></label>
            </div>
          </section>
        </div>
      </div>
    </form>
  );
}
